import { motion, AnimatePresence } from "framer-motion";
import { ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { MOTION, toSeconds } from "@/lib/uiConstants";

interface PageTransitionProps {
  children: ReactNode;
}

/**
 * PageTransition - Simple fade transition for standard page navigation.
 * 
 * ANIMATION RULE: Keys by BASE route only (first segment), not full pathname.
 * This prevents page re-animation when opening/closing overlays.
 * 
 * Examples:
 * - /orders and /orders/new → same key "orders" → no page animation
 * - /orders to /products → different keys → page animates
 * 
 * NOTE: Overlay transitions are handled by OverlayPage component directly.
 * 
 * ACCESSIBILITY: Respects prefers-reduced-motion by disabling all transitions.
 */
export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();

  // Extract base route for animation key
  // /orders/new/123 → "orders"
  // /products → "products"  
  // / → "home"
  const baseRoute = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments[0] || 'home';
  }, [location.pathname]);

  // Skip all animations for reduced-motion users
  if (shouldReduceMotion) {
    return (
      <div style={{ minHeight: "100vh", width: "100%" }}>
        {children}
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={baseRoute}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ 
          duration: toSeconds(MOTION.duration.normal),
          ease: MOTION.easing.standard
        }}
        style={{
          minHeight: "100vh",
          width: "100%"
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
